import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
// import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { Avatar, Box, Chip } from "@mui/material";
import { GridColDef, GridSortItem } from "@mui/x-data-grid";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  Suspense,
  useEffect,
  useState,
} from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex, { FlexProps } from "../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../components/GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import Text from "../../../../../components/Text";
import ViewAppointment from "../../../../../components/ViewAppointment";
import { NEW_APPOINTMENT_VISIT_TYPE_MAPPER } from "../../../../../constants/scheduling";
import {
  Action,
  // APPOINTMENT_LIST_SCHEDULING,
  AppointmentSchedulingType,
} from "../../../../../dummy-data/appointment-list-data-scheduling";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import {
  NonNullableDate,
  formatTime,
} from "../../../../../utils/date-time-utility";
import {
  DEFAULT_FONTSIZE,
  DGT_Pagination,
  PURPLE_ACCENT_COLORS,
  BLUE_ACCENT_COLORS,
  GREEN_ACCENT_COLORS,
  GRAY_ACCENT_COLORS,
  applyDefaultColumnSettings,
  DEFAULT_SECONDARY_TEXT_COLOR,
  getInitials,
  getAge,
  replaceUnderScoreAddSpace,
  DEFAULT_TEXT_COLOR,
  ORANGE_ACCENT_COLORS,
  LIGHT_GREEN_ACCENT_COLORS,
  RED_ACCENT_COLORS,
  YELLOW_ACCENT_COLORS,
} from "../../../../../utils/utils";
import lazyWithRetry from "../../../../../utils/lazy-with-retry";
import { SHORT_GENDER_NAME_MAPPER } from "../../../../../constants";
import {
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "../../../../../components/app/provider/patient/chart";

const CheckIn = lazyWithRetry(() => import("./CheckIn"));

const GENDER_AVATAR_STYLES = {
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "2px solid white",
  fontSize: "12px",
  fontWeight: "bold",
  color: "#666",
  bgcolor: "#e0e0e0",
} as const;

export const VISIT_TYPE_MAPPER: Record<
  AppointmentSchedulingType["mode"],
  ReactNode
> = {
  IN_PERSON: (
    <Chip
      size="small"
      style={{
        fontSize: DEFAULT_FONTSIZE.fontSize,
        backgroundColor: BLUE_ACCENT_COLORS.backgroundColor,
        color: BLUE_ACCENT_COLORS.textColor,
        borderRadius: BLUE_ACCENT_COLORS.borderRadius,
        padding: BLUE_ACCENT_COLORS.padding,
      }}
      label={NEW_APPOINTMENT_VISIT_TYPE_MAPPER["IN_PERSON"]}
      icon={<PersonOutlinedIcon color={BLUE_ACCENT_COLORS.iconColor as any} />}
    />
  ),
  VIRTUAL: (
    <Chip
      size="small"
      style={{
        fontSize: DEFAULT_FONTSIZE.fontSize,
        backgroundColor: PURPLE_ACCENT_COLORS.backgroundColor,
        color: PURPLE_ACCENT_COLORS.textColor,
        borderRadius: PURPLE_ACCENT_COLORS.borderRadius,
        padding: PURPLE_ACCENT_COLORS.padding,
      }}
      label={NEW_APPOINTMENT_VISIT_TYPE_MAPPER["VIRTUAL"]}
      icon={
        <VideocamOutlinedIcon color={PURPLE_ACCENT_COLORS.iconColor as any} />
      }
    />
  ),
};

export const COLOR_MAPPER: Record<Action, { color: string; bgColor: string }> =
  {
    "Signed Off": {
      color: LIGHT_GREEN_ACCENT_COLORS.textColor,
      bgColor: LIGHT_GREEN_ACCENT_COLORS.backgroundColor,
    },
    "Checked In": {
      color: PURPLE_ACCENT_COLORS.textColor,
      bgColor: PURPLE_ACCENT_COLORS.backgroundColor,
    },
    Confirmed: {
      color: GREEN_ACCENT_COLORS.textColor,
      bgColor: GREEN_ACCENT_COLORS.backgroundColor,
    },
    "In Exam": {
      color: ORANGE_ACCENT_COLORS.textColor,
      bgColor: ORANGE_ACCENT_COLORS.backgroundColor,
    },
    Scheduled: {
      color: BLUE_ACCENT_COLORS.textColor,
      bgColor: BLUE_ACCENT_COLORS.backgroundColor,
    },
    Canceled: {
      color: RED_ACCENT_COLORS.textColor,
      bgColor: RED_ACCENT_COLORS.backgroundColor,
    },
    "No Show": {
      color: GRAY_ACCENT_COLORS.textColor,
      bgColor: GRAY_ACCENT_COLORS.backgroundColor,
    },
    Instant: {
      color: YELLOW_ACCENT_COLORS.textColor,
      bgColor: YELLOW_ACCENT_COLORS.backgroundColor,
    },
  };

export const APPOINTMENT_STATUS_RECORD: Record<
  AppointmentSchedulingType["status"],
  Action
> = {
  PENDING: "Scheduled",
  CONFIRMED: "Confirmed",
  CANCELLED: "Canceled",
  NO_SHOW: "No Show",
  CHECKED_IN: "Checked In",
  IN_EXAM: "In Exam",
  COMPLETED: "Signed Off",
  INSTANT: "Instant",
  // SCHEDULED:""
};

interface AppointmentSchedulingContext {
  selectedDate: any;
  provider: { value: any; label: string }[] | undefined;
  patient: string | null;
  appointmentsList: any;
  pagination: DGT_Pagination;
  setPagination: Dispatch<SetStateAction<DGT_Pagination>>;
  gridSorting: GridSortItem[];
  setGridSorting: Dispatch<SetStateAction<GridSortItem[]>>;
  selectedAppointment: AppointmentSchedulingType | undefined;
  setSelectedAppointment: React.Dispatch<
    React.SetStateAction<AppointmentSchedulingType | undefined>
  >;
  openChangeStatus: (statusChanged: "CANCELLED" | "NO_SHOW") => void;
  openReschedule: () => void;
  appointmentDisclosure?: DisclosureHost;
  isLoading?: boolean;
}

const AppointmentScheduling = () => {
  const checkInDrawer = useDisclosure();
  const {
    selectedAppointment,
    appointmentsList,
    pagination,
    setPagination,
    gridSorting,
    setGridSorting,
    setSelectedAppointment,
    openChangeStatus,
    openReschedule,
    appointmentDisclosure,
    isLoading,
  }: AppointmentSchedulingContext = useOutletContext();
  const [value, setValue] = useState("appointment");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (
      searchParams.get("openCheckIn") === "true" &&
      searchParams.get("patientId") !== null
    ) {
      setSelectedAppointment(
        JSON.parse(localStorage.getItem("appointmentData") ?? "{}")
      );
      checkInDrawer.open();
    }
  }, [searchParams]);

  const COLUMNS: GridColDef<AppointmentSchedulingType>[] =
    applyDefaultColumnSettings([
      {
        field: "appointmentDate",
        flex: 0.5,
        minWidth: 100,
        headerName: "Date",
        sortable: true,
        renderCell: ({ row }) => {
          return (
            <Flex flexDirection={"column"}>
              <Text isTabletext c={DEFAULT_SECONDARY_TEXT_COLOR}>
                {NonNullableDate(new Date(row?.startTime ?? ""))}
              </Text>
            </Flex>
          );
        },
      },
      {
        field: "startTime",
        flex: 0.5,
        minWidth: 120,
        headerName: "Time & Duration",
        sortable: true,
        renderCell: ({ row }) => {
          return (
            <Flex flexDirection={"column"}>
              <Flex gap={0.6}>
                <Text
                  isTabletext
                  marginBottom={"3px"}
                  c={DEFAULT_SECONDARY_TEXT_COLOR}
                >
                  {row?.startTime ? formatTime(row?.startTime) : "-"}
                </Text>
                {!!row?.duration && (
                  <Text
                    isTabletext
                    c={DEFAULT_SECONDARY_TEXT_COLOR}
                  >{`| ${row?.duration} Mins`}</Text>
                )}
              </Flex>
            </Flex>
          );
        },
      },
      {
        field: "mode",
        flex: 0.5,
        minWidth: 110,
        headerName: "Visit Type",
        sortable: false,
        renderCell: ({ row }) => VISIT_TYPE_MAPPER[row.mode],
      },
      {
        field: "patientName",
        flex: 1,
        minWidth: 150,
        headerName: "Patient Name",
        renderCell: ({ row, api }) => {
          const name = row?.patientName ?? "";
          const rowIndex = api.getRowIndexRelativeToVisibleRows(row.uuid);
          const isOddRow = rowIndex % 2 !== 0;

          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar
                sx={{
                  ...GENDER_AVATAR_STYLES,
                  bgcolor: "#E7E7E7",
                  color: DEFAULT_TEXT_COLOR,
                  border: isOddRow ? "transparent" : "#E7E7E7",
                  fontSize: "10px",
                  fontWeight: "400",
                  width: "20px",
                  height: "20px",
                }}
              >
                {getInitials(name)}
              </Avatar>
              <Box>
                <Text
                  marginBottom={"1px"}
                  isTabletext
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    if (row.patientId)
                      navigate(
                        `/app/provider/patients/new-patient/${row.patientId}/index`
                      );
                  }}
                >
                  {`${row?.patientName}`}
                </Text>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "patientDob",
        flex: 0.4,
        minWidth: 100,
        headerName: "Age & Gender",
        sortable: false,
        renderCell: ({ row }: { row: AppointmentSchedulingType }) =>
          `${getAge(row?.patientDob)}${row?.patientGender ? "(" + SHORT_GENDER_NAME_MAPPER[row?.patientGender] + ")" : "-"}`,
      },
      {
        field: "appointmentType",
        flex: 0.6,
        minWidth: 120,
        headerName: "Appointment Type",
        sortable: false,
        renderCell: ({ row }) =>
          row?.type ? replaceUnderScoreAddSpace(row?.type?.toLowerCase()) : "-",
      },
      {
        field: "providerName",
        flex: 0.7,
        minWidth: 120,
        headerName: "Provider",
        renderCell: ({ row }) => row.providerName || "-",
        sortable: false,
      },
      {
        field: "chiefComplaint",
        flex: 0.6,
        minWidth: 110,
        headerName: "Reason for Visit",
        sortable: false,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      // {
      //   field: "locationName",
      //   flex: 1,
      //   minWidth: 150,
      //   headerName: "Location",
      //   sortable: false,
      //   renderCell: ({ value }) => value || "-",
      // },
      {
        field: "FormStatus",
        flex: 0.5,
        minWidth: 100,
        headerName: "Form Status",
        sortable: false,
        renderCell: ({ row }) => {
          const status = row?.emailForms;
          const accentMap: Record<
            string,
            {
              textColor: string;
              backgroundColor: string;
              borderRadius: string;
              padding: string;
            }
          > = {
            Sent: BLUE_ACCENT_COLORS,
            Submitted: GREEN_ACCENT_COLORS,
            "Not Sent": GRAY_ACCENT_COLORS,
          };
          const accent =
            status && accentMap[status]
              ? accentMap[status]
              : GRAY_ACCENT_COLORS;
          return status ? (
            <Chip
              size="small"
              sx={{
                color: accent.textColor,
                backgroundColor: accent.backgroundColor,
                borderRadius: accent.borderRadius,
                padding: accent.padding,
              }}
              label={<Text isTabletext>{status}</Text>}
            />
          ) : (
            ""
          );
          // <Box>
          //   <Text>
          //     {row?.emailedFormsStatus ? "Submitted" : "Not Submitted"}
          //   </Text>
          //   {/* <Link sx={{ cursor: "pointer", textDecoration: "none" }}> */}
          //   <Text sx={{ cursor: "pointer" }} color={"primary"}>
          //     {row.emailedFormsStatus ? "View" : "Send"}
          //   </Text>
          //   {/* </Link> */}
          //   {/* <Typography color={"secondary"}>
          //     {value == "Submitted" ? "View" : "Send"}
          //   </Typography> */}
          // </Box>
        },
      },
      {
        field: "insuranceEligibility",
        flex: 0.4,
        minWidth: 90,
        headerName: "Ins. Eligibility",
        sortable: false,
        align: "center",
        renderCell: ({ value }) => (
          // <RuleFolderIcon color={value ? "success" : "error"} />
          <Text isTabletext c={DEFAULT_SECONDARY_TEXT_COLOR}>
            {value ? "Yes" : "No"}
          </Text>
        ),
      },
      {
        field: "status",
        flex: 0.6,
        minWidth: 120,
        headerName: "Action",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <CustomActionButton
              height={"26px"}
              width={"110px"}
              label={APPOINTMENT_STATUS_RECORD[row.status]}
              onClick={() => {
                appointmentDisclosure?.open();
                setSelectedAppointment(row);
              }}
            />
          );
        },
      },
    ]);

  // function onSubmitPayment(values: PaymentMethodType) {
  //   console.log(values);
  //   paymentMethodDrawer.open();
  // }

  // const selectedLocations = accordionFilterOptions.Locations.optList.filter(
  //   (location: any) => location.selected
  // );
  const navigate = useNavigate();

  return (
    <Box mr={1}>
      {/* {selectedLocations.length > 0 ? (
        <>
          {selectedLocations.map((location: any, index: number) => (
            <Fragment key={index}>
              <CustomTitle mb={"10px"}>{location?.label}</CustomTitle>

              <CustomDataGridV2
                searchString=""
                rows={appointmentsList?.data?.content ?? []}
                columns={COLUMNS}
                sx={{ marginTop: "40px" }}
                disableRowSelectionOnClick
                striped
                paginationModel={masterPagination[index]}
                onPaginationModelChange={(model) => {
                  const updatedPagination = [...masterPagination];
                  updatedPagination[index] = model;
                  setMasterPagination(updatedPagination);
                }}
                getRowId={(row) => row.uuid}
              />
            </Fragment>
          ))}
        </>
      ) : (
        <CustomDataGridV2
          searchString=""
          rows={appointmentsList?.data?.content ?? []}
          columns={COLUMNS}
          sx={{ marginTop: "40px" }}
          disableRowSelectionOnClick
           
          hideFooter={true}
          getRowId={(row) => row.uuid}
        />
      )} */}
      {/* todo need to check height once pagination add  */}
      <GlobalTableHeightWrapper height="calc(100vh - 8.3rem)">
        <CustomDataGridV2
          loading={isLoading}
          rowHeight={42}
          getRowId={(row) => row?.uuid}
          rows={appointmentsList?.data?.content ?? []}
          rowCount={appointmentsList?.data?.page?.totalElements ?? 10}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={gridSorting}
          onSortModelChange={setGridSorting}
          disableRowSelectionOnClick
        />
      </GlobalTableHeightWrapper>
      <ViewAppointment
        open={!!appointmentDisclosure?.opened}
        onClose={appointmentDisclosure?.close}
        activeTab={value}
        handleTabChange={(val) => setValue(val)}
        openChangeStatus={openChangeStatus}
        openRescheduleAppointment={openReschedule}
        openCheckIn={checkInDrawer.open}
        appointmentUuid={selectedAppointment?.uuid}
      />
      {checkInDrawer.opened && (
        <Suspense fallback={null}>
          <CheckIn
            disclosureHost={checkInDrawer}
            patientId={selectedAppointment?.patientId}
            appointmentUuid={selectedAppointment?.uuid}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default AppointmentScheduling;

const CustomActionButton = ({
  label,
  onClick,
  ...others
}: {
  label: Action;
  onClick: (v: string) => void;
} & FlexProps) => {
  return (
    <Box
      sx={{
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          opacity: 0.85,
          transform: "scale(1.02)",
        },
        display: "flex",
        alignItems: "center",
        bgcolor: COLOR_MAPPER[label]?.bgColor,
        borderRadius: "5px",
        overflow: "hidden",
        width: others.width || "110px",
        height: others.height || "26px",
        ...others.sx,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(label);
      }}
    >
      <Text
        fontSize={CHART_TYPOGRAPHY.body5.fontSize}
        c={COLOR_MAPPER[label]?.color}
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          pl: 1,
        }}
      >
        {label}
      </Text>
      <Box
        sx={{
          width: "26px",
          minWidth: "26px",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: COLOR_MAPPER[label]?.color,
        }}
      >
        <ChevronRightIcon
          sx={{ color: "white", fontSize: CHART_DIMENSIONS.iconMd }}
        />
      </Box>
    </Box>
  );
};
